import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Grid, Stack } from '@mui/material';
import { sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row } from 'react-table';

import { Collapse } from '@work4all/components/lib/components/collapse';

import { useDataMutation } from '@work4all/data';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { InputArticlePurchasePriceUpsert } from '@work4all/models/lib/Classes/InputArticlePurchasePriceUpsert.entity';
import { InputArticleRelation } from '@work4all/models/lib/Classes/InputArticleRelation.entity';
import { PurchasePrice } from '@work4all/models/lib/Classes/PurchasePrice.entity';
import { ArticleKind } from '@work4all/models/lib/Enums/ArticleKind.enum';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { ControllerPlus } from '../../../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../../../form-plus/use-form-context-plus';
import { CheckboxRadioItem, IconButton } from '../../../../../locked-inputs';
import { UpdateEntity } from '../../../../components/update-entity/UpdateEntity';
import { useMaskContext } from '../../../../hooks/mask-context';
import { useConfirmDeleteEntities } from '../../../../hooks/use-confirm-delete-entities';
import { CurrencyExchangeInfoContext } from '../../../inbound-invoice/currency-exchange-info-context';
import { ArticleMaskFormValue } from '../../types';
import { useArticleRequest } from '../../use-article-request';

import { PurchasePricePopover } from './components/purchase-price-popover/PurchasePricePopover';
import { PurchasePriceTable } from './PurchasePriceTable';
import { SinglePriceTable } from './SinglePriceTable';

interface PricesTabPanelProps {
  handleSubmitWithoutClose?: (event: React.FormEvent) => Promise<void>;
}

export function PricesTabPanel({
  handleSubmitWithoutClose,
}: PricesTabPanelProps) {
  const { t } = useTranslation();

  const confirmDeleteEntities = useConfirmDeleteEntities({ standard: true });

  const form = useFormContextPlus<ArticleMaskFormValue>();

  const mask = useMaskContext();
  const isCreateMode = mask.isCreateMode;

  const standardAsEntry = form.watch('standardPurchasePriceAsEntryPrice');

  const articleId = form.watch('id');
  const articleName = form.watch('name');
  const articleNumber = form.watch('number');
  const entryPrice = form.watch('entryPrice');
  const articlePrices = form.watch('articlePrices');

  const singlePriceList = articlePrices?.singlePriceList;
  const singlePriceListSorted = useMemo(() => {
    return sortBy(singlePriceList ?? [], (x) => x.priceGroup?.id);
  }, [singlePriceList]);

  const purchasePriceList = articlePrices?.purchasePriceList ?? [];
  const takenSupplierIds = purchasePriceList.map((item) => item.supplierId);

  const [selectedPurchasePriceRow, setSelectedPurchasePriceRow] =
    useState<PurchasePrice>(null);
  const [newMode, setNewMode] = useState(false);

  const request = useArticleRequest({});

  const [mutate] = useDataMutation<
    ArticleMaskFormValue,
    EMode.upsert,
    InputArticleRelation
  >({
    entity: Entities.article,
    mutationType: EMode.upsert,
    responseData: request.data,
    onCompleted: (data) => {
      form.setValue('articlePrices', data.articlePrices, {
        shouldDirty: false,
      });
      popoverState.onClose();
    },
  });

  const handlePurchasePriceSelection = (item?: Row[]) => {
    setSelectedPurchasePriceRow(item?.[0]?.original ?? null);
  };

  const handleEdit = () => {
    setNewMode(false);
    popoverState.handleClick();
  };

  const handleAdd = () => {
    setNewMode(true);
    popoverState.handleClick();
  };

  const handleDelete = async () => {
    const confirmed = await confirmDeleteEntities({ count: 1 });

    if (!confirmed) return;

    const relations: InputArticleRelation = {
      prices: {
        purchasePrices: {
          remove: [selectedPurchasePriceRow.id],
        },
      },
    };

    await mutate({ id: articleId }, { relations });
    setSelectedPurchasePriceRow(null);

    if (
      form.watch('standardSupplierId') === selectedPurchasePriceRow.supplier?.id
    ) {
      form.setValue('standardSupplierId', 0, {
        shouldDirty: true,
      });
    }
  };

  const popoverState = usePopoverState();

  const currencyExchangeInfo = useContext(CurrencyExchangeInfoContext);
  const defaultCurrency = currencyExchangeInfo?.defaultCurrency;

  const handleSubmitPurchasePrice = async (
    submitData: PurchasePrice,
    isStandardPurchasePrice: boolean
  ) => {
    const relations: InputArticleRelation = {
      prices: {
        purchasePrices: {
          addOrModify: [
            {
              amountPerUnit: submitData.amountPerUnit,
              amountToOrder: submitData.orderAmount,
              articleNumber: submitData.articleNumber,
              discount: submitData.discount,
              currencyCode: submitData.currency?.id ?? defaultCurrency.id,
              listprice: submitData.listPrice,
              note: submitData.note,
              purchasePrice: submitData.purchasePrice,
              supplierCode: submitData.supplier?.id,
              supplierText: submitData.supplierText,
              unitCode: submitData.unit?.id,
              deliveryTime: submitData.deliveryTime,
              code: submitData.id,
              factor: submitData.factor,
              validFrom: submitData.validFromDate
                ? DateTime.fromISO(submitData.validFromDate).toFormat(
                    'yyyy-MM-dd'
                  )
                : MIN_SQL_DATE_VALUE,
            } satisfies InputArticlePurchasePriceUpsert,
          ],
        },
      },
    };

    await mutate(
      {
        id: articleId,
        ...(isStandardPurchasePrice && {
          standardSupplierId: submitData.supplier?.id,
        }),
      },
      { relations }
    );

    if (isStandardPurchasePrice) {
      form.setValue('standardSupplierId', submitData.supplier?.id, {
        shouldDirty: false,
      });
    }
  };

  const isBOM = form.watch('articleKind') === ArticleKind.STUECKLISTE;

  return (
    <Box sx={{ py: '1rem' }}>
      <Grid container columns={1}>
        <Grid item xs={1}>
          <Collapse
            title={t('MASK.ARTICLE.SELLING_PRICES')}
            defaultOpen
            bottomIndent="lg"
          >
            <SinglePriceTable
              entryPrice={entryPrice}
              showFixedPrice={isBOM}
              entryPriceDisabled={Boolean(standardAsEntry)}
              onEntryPriceChange={(value) => {
                form.setValue('entryPrice', value, { shouldDirty: true });
              }}
              singlePriceList={singlePriceListSorted}
              onChange={(singlePriceList) => {
                form.setValue(
                  'articlePrices',
                  { ...articlePrices, singlePriceList },
                  { shouldDirty: true }
                );
              }}
            />
          </Collapse>
        </Grid>

        <Grid item xs={1}>
          <Collapse
            title={t('MASK.ARTICLE.PURCHASE_PRICES')}
            defaultOpen
            bottomIndent="lg"
            headerBar={
              <Stack direction="row" mx={0.5}>
                {isCreateMode ? (
                  <UpdateEntity
                    iconSize="1.5rem"
                    onSubmit={handleSubmitWithoutClose}
                  />
                ) : (
                  <>
                    <IconButton color="primary" onClick={handleAdd}>
                      <AddIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={handleEdit}
                      disabled={!selectedPurchasePriceRow}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={handleDelete}
                      disabled={!selectedPurchasePriceRow}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Stack>
            }
          >
            <Grid container columns={1} spacing="1rem">
              <Grid item xs={1}>
                <PurchasePriceTable
                  standardSupplierId={form.watch('standardSupplierId')}
                  onStandardSupplierIdChange={(value) => {
                    form.setValue('standardSupplierId', value, {
                      shouldDirty: true,
                    });
                  }}
                  purchasePriceList={purchasePriceList}
                  onSelectedRowsChange={handlePurchasePriceSelection}
                  onRowDoubleClick={handleEdit}
                />
              </Grid>

              <Grid item xs={1}>
                <ControllerPlus
                  control={form.control}
                  name="standardPurchasePriceAsEntryPrice"
                  render={({ field: { value, ...field } }) => {
                    const checked = Boolean(value);

                    return (
                      <CheckboxRadioItem
                        control="checkbox"
                        disabled={purchasePriceList.length === 0}
                        label={t(
                          'MASK.ARTICLE.FIELDS.standardPurchasePriceAsEntryPrice'
                        )}
                        {...field}
                        checked={checked}
                        defaultChecked={checked}
                        onChange={(event) => {
                          field.onChange(Number(event.target.checked));
                        }}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Grid>

        <Grid item xs={1}>
          <Collapse
            title={t('MASK.ARTICLE.DISCOUNTS_AND_COMMISSIONS')}
            defaultOpen
            bottomIndent="lg"
          >
            <Grid container columns={4} spacing="1rem">
              {/* <Grid item xs={1}>
                <LabeledInput disabled label={t('Rabattgruppe')} value="Ohne" />
              </Grid> */}

              <Grid item xs={1}>
                <ControllerPlus
                  control={form.control}
                  name="discountable"
                  render={({ field: { value: checked, ...field } }) => {
                    return (
                      <CheckboxRadioItem
                        control="checkbox"
                        label={t('MASK.ARTICLE.FIELDS.discountable')}
                        checked={checked}
                        defaultChecked={checked}
                        {...field}
                      />
                    );
                  }}
                />
              </Grid>

              <Grid item xs={1}>
                <ControllerPlus
                  control={form.control}
                  name="noSkonto"
                  render={({ field: { value, ...field } }) => {
                    const checked = !value;

                    return (
                      <CheckboxRadioItem
                        control="checkbox"
                        label={t('MASK.ARTICLE.FIELDS.noSkonto')}
                        {...field}
                        checked={checked}
                        defaultChecked={checked}
                        onChange={(event) => {
                          field.onChange(!event.target.checked);
                        }}
                      />
                    );
                  }}
                />
              </Grid>

              <Grid item xs={1}>
                <ControllerPlus
                  control={form.control}
                  name="hasNoCommission"
                  render={({ field: { value, ...field } }) => {
                    const checked = !value;

                    return (
                      <CheckboxRadioItem
                        control="checkbox"
                        label={t('MASK.ARTICLE.FIELDS.hasNoCommission')}
                        {...field}
                        checked={checked}
                        defaultChecked={checked}
                        onChange={(event) => {
                          field.onChange(!event.target.checked);
                        }}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Grid>
      </Grid>

      <PurchasePricePopover
        selectedElement={newMode ? null : selectedPurchasePriceRow}
        takenSupplierIds={takenSupplierIds}
        onSubmit={handleSubmitPurchasePrice}
        popoverState={popoverState}
        article={{
          name: articleName,
          number: articleNumber,
        }}
      />
    </Box>
  );
}

// 'null' is the 'not set' indicator in this case, so we do the trick with the minimum SQL date value
// https://learn.microsoft.com/en-us/dotnet/api/system.data.sqltypes.sqldatetime.minvalue?view=net-9.0
const MIN_SQL_DATE_VALUE = '1752-12-31';
